import React, { useEffect, useState } from 'react';
import {
  clearEmployeeTimeToken,
  employeeClockIn,
  employeeClockOut,
  employeeLogin,
  employeeLogout,
  fetchEmployeeStatus,
  getEmployeeTimeToken,
  setEmployeeTimeToken,
  type EmployeeStatusResponse,
} from '../api/timeTracking';
import { borderRadius, colors, spacing, typography, typographyFlat } from '../styles/tokens';
import { formatInstant } from '../utils/datetime';

const formatClock = (iso: string | undefined) => formatInstant(iso, '-');

export const TimeClockPage: React.FC = () => {
  const [employeeCode, setEmployeeCode] = useState('');
  const [pin, setPin] = useState('');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<EmployeeStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearKioskSession = () => {
    clearEmployeeTimeToken();
    setStatus(null);
    setEmployeeCode('');
    setPin('');
    setNote('');
  };

  const loadStatus = async (token: string) => {
    const nextStatus = await fetchEmployeeStatus(token);
    setStatus(nextStatus);
  };

  useEffect(() => {
    const token = getEmployeeTimeToken();
    if (!token) return;

    void (async () => {
      try {
        setLoading(true);
        await loadStatus(token);
      } catch {
        clearEmployeeTimeToken();
        setStatus(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleLogin = async () => {
    setError(null);
    try {
      setLoading(true);
      const response = await employeeLogin(employeeCode.trim(), pin);
      setEmployeeTimeToken(response.token);
      await loadStatus(response.token);
      setPin('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    const token = getEmployeeTimeToken();
    if (!token) {
      setError('Please log in first');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await employeeClockIn(token, note.trim() || undefined);
      try {
        await employeeLogout(token);
      } catch {
        // Ignore logout failures after a successful clock action.
      }
      clearKioskSession();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Clock in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    const token = getEmployeeTimeToken();
    if (!token) {
      setError('Please log in first');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await employeeClockOut(token, note.trim() || undefined);
      try {
        await employeeLogout(token);
      } catch {
        // Ignore logout failures after a successful clock action.
      }
      clearKioskSession();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Clock out failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const token = getEmployeeTimeToken();
    try {
      if (token) {
        await employeeLogout(token);
      }
    } catch {
      // ignore logout failures and clear local token
    } finally {
      clearKioskSession();
    }
  };

  // Render component
  if (!status) {
    // Login form
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Employee Time Clock</h1>
          <p style={styles.subtitle}>Clock in and out with your employee code and PIN</p>
        </div>

        <div style={styles.card}>
          <div style={styles.form}>

            <label style={styles.label}>
              Employee Code
              <input
                style={styles.input}
                value={employeeCode}
                onChange={e => setEmployeeCode(e.target.value)}
                placeholder="E.g., E100"
                autoComplete="off"
                inputMode="text"
                data-lpignore="true"
                data-form-type="other"
                data-1p-ignore="true"
              />
            </label>

            <label style={styles.label}>
              PIN
              <input
                type="text"
                style={styles.input}
                value={pin}
                onChange={e => setPin(e.target.value)}
                placeholder="4+ digits"
                autoComplete="off"
                inputMode="numeric"
                data-lpignore="true"
                data-form-type="other"
                data-1p-ignore="true"
              />
            </label>

            <button
              type="button"
              style={styles.primaryButton}
              disabled={loading}
              onClick={() => { void handleLogin(); }}
            >
              {loading ? 'Logging in...' : 'Clock In'}
            </button>
          </div>

          {error && <div style={styles.error}>{error}</div>}
        </div>
      </div>
    );
  }

  // Logged-in view
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Employee Time Clock</h1>
        <p style={styles.subtitle}>Welcome, {status.display_name}</p>
      </div>

      <div style={styles.card}>
        <div style={styles.statusRow}>
          <span>Status:</span>
          <span style={status.currently_clocked_in ? styles.statusBadgeActive : styles.statusBadgeInactive}>
            {status.currently_clocked_in ? 'Clocked In' : 'Clocked Out'}
          </span>
        </div>

        {status.currently_clocked_in && status.active_entry && (
          <>
            <div style={styles.detailRow}>
              <span>Clocked in at:</span>
              <span>{formatClock(status.active_entry.clock_in_at)}</span>
            </div>
            <div style={styles.detailRow}>
              <span>Elapsed time:</span>
              <span>{(status.active_entry.elapsed_minutes / 60).toFixed(1)} hours</span>
            </div>
          </>
        )}
      </div>

      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>Notes (optional)</h2>
        <textarea
          style={styles.textarea}
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Add a note (e.g., 'Working on inventory')"
          name="clock_note"
          autoComplete="off"
        />
      </div>

      <div style={styles.card}>
        <div style={styles.actionRow}>
          {!status.currently_clocked_in && (
            <button
              type="button"
              style={styles.primaryButton}
              onClick={handleClockIn}
              disabled={loading}
            >
              {loading ? 'Clocking in...' : 'Clock In'}
            </button>
          )}

          {status.currently_clocked_in && (
            <button
              type="button"
              style={styles.primaryButton}
              onClick={handleClockOut}
              disabled={loading}
            >
              {loading ? 'Clocking out...' : 'Clock Out'}
            </button>
          )}

          <button
            type="button"
            style={styles.secondaryButton}
            onClick={handleLogout}
            disabled={loading}
          >
            Logout
          </button>
        </div>

        {error && <div style={styles.error}>{error}</div>}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'grid',
    gap: spacing.lg,
    maxWidth: '760px',
  },
  header: {
    display: 'grid',
    gap: spacing.xs,
  },
  card: {
    width: '100%',
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: spacing.lg,
  },
  title: {
    margin: 0,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
  },
  subtitle: {
    margin: 0,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  sectionTitle: {
    margin: 0,
    color: colors.text,
    fontSize: typography.h3.fontSize,
    fontWeight: typography.h3.fontWeight,
  },
  sectionSubtitle: {
    marginTop: spacing.xs,
    marginBottom: spacing.md,
    color: colors.textSecondary,
    fontSize: typographyFlat.fontSize.sm,
  },
  form: {
    display: 'grid',
    gap: spacing.md,
  },
  label: {
    display: 'grid',
    gap: spacing.xs,
    color: colors.text,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.medium,
  },
  input: {
    width: '100%',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typographyFlat.fontSize.md,
    color: colors.text,
  },
  textarea: {
    width: '100%',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typographyFlat.fontSize.md,
    color: colors.text,
    resize: 'vertical',
    minHeight: '72px',
  },
  loggedInArea: {
    display: 'grid',
    gap: spacing.md,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    fontSize: typographyFlat.fontSize.lg,
    color: colors.text,
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    color: colors.textSecondary,
    fontSize: typographyFlat.fontSize.sm,
  },
  statusBadgeActive: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    borderRadius: borderRadius.full,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typographyFlat.fontSize.xs,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  statusBadgeInactive: {
    backgroundColor: '#eceff1',
    color: '#455a64',
    borderRadius: borderRadius.full,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typographyFlat.fontSize.xs,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  actionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  primaryButton: {
    border: `1px solid ${colors.primary}`,
    backgroundColor: colors.primary,
    color: colors.white,
    borderRadius: borderRadius.md,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  secondaryButton: {
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
    color: colors.text,
    borderRadius: borderRadius.md,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  linkButton: {
    border: 'none',
    background: 'transparent',
    color: colors.primary,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  error: {
    marginTop: spacing.md,
    backgroundColor: colors.errorLight,
    border: `1px solid ${colors.error}`,
    color: colors.error,
    borderRadius: borderRadius.md,
    padding: spacing.sm,
    fontSize: typographyFlat.fontSize.sm,
  },
  tableWrap: {
    marginTop: spacing.lg,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    overflowX: 'auto',
    backgroundColor: colors.surface,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.textSecondary,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.semibold,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: typographyFlat.fontSize.sm,
    verticalAlign: 'top',
  },
  tdStrong: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.semibold,
    verticalAlign: 'top',
  },
  emptyCell: {
    padding: spacing.lg,
    textAlign: 'center',
    color: colors.textSecondary,
  },
  inlineInput: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typographyFlat.fontSize.sm,
    color: colors.text,
    minWidth: '120px',
  },
};
